<template>
  <div class="my-10">
    <div class="flex flex-col gap-10">
      <div class="grid grid-cols-2 gap-5 max-lg:grid-cols-1">
        <div class="flex flex-col">
          <!-- MAIN MEDIA (как в viewMyPublication.vue) -->
          <div class="w-full h-[25rem] rounded-lg overflow-hidden">
            <video
              v-if="isVideo(currentImage)"
              controls
              controlsList="nodownload"
              :src="currentImage"
              class="h-full w-full object-contain"
            ></video>

            <img
              v-else
              :src="currentImage || '/placeholder-publication.png'"
              alt="main-image"
              class="h-full w-full object-fill rounded-lg cursor-pointer"
              @click="showSingle"
              loading="lazy"
              decoding="async"
              @error="handleImageError"
            />

            <vue-easy-lightbox
              :imgs="imgsRef"
              :index="indexRef"
              :visible="visibleRef"
              @hide="onHide"
            />
          </div>

          <!-- THUMB CAROUSEL (как в viewMyPublication.vue) -->
          <div v-if="(detail?.images || []).length">
            <Splide :has-track="false" :options="imageOptions" aria-label="carouselBanner">
              <SplideTrack class="rounded-lg py-2">
                <SplideSlide v-for="(item, index) in detail?.images || []" :key="item.id || index">
                  <div
                    class="h-32 w-full rounded-md overflow-hidden cursor-pointer"
                    @click="changeImage(index)"
                  >
                    <video
                      v-if="isVideo(item?.path)"
                      :src="item?.path"
                      class="h-full w-full object-contain pointer-events-none"
                      muted
                      playsinline
                      preload="metadata"
                    ></video>

                    <img
                      v-else
                      :src="item?.path"
                      class="h-full w-full object-fill pointer-events-none"
                      loading="lazy"
                      decoding="async"
                      @error="handleImageError"
                      alt=""
                    />
                  </div>
                </SplideSlide>
              </SplideTrack>
            </Splide>
          </div>
        </div>
        <div>
          <div class="border-b border-[#E3E3E3] mb-4">
            <div class="flex items-center gap-3 mb-3">
              <div class="flex-1 min-w-0" ref="titleWrap">
                <p
                  ref="titleEl"
                  class="w-full text-main-color font-bold leading-tight whitespace-nowrap overflow-hidden text-ellipsis"
                  :style="{ fontSize: titleFont + 'px' }"
                >
                  {{ titleText }}
                </p>
              </div>
            </div>
            <div class="mb-2">
              <p>
                {{ $t('page.category') }}:
                <router-link
                  class="text-lg font-medium hover:text-accent-color"
                  :to="{ name: 'publications', query: { category: detail?.category_id } }"
                  >{{ detail?.category }}</router-link
                >
              </p>
            </div>
            <div class="flex items-center gap-5 mb-3">
              <p v-if="categoryDetail?.has_price" class="text-[#409DBA] font-semibold text-2xl">
                {{ formatNumber(detail?.price) }} TMT
              </p>
            </div>

            <div class="flex mb-2 font-light text-main-color text-base">
              <p>{{ detail?.location }}</p>
              <div class="flex items-center">
                <div class="border-l mx-2 h-5"></div>
                <p>{{ detail?.date_created }}</p>
              </div>
            </div>
            <div class="mb-2">
              <a class="hover:text-accent-color" :href="`tel:${detail?.phone}`">{{
                detail?.phone
              }}</a>
            </div>
          </div>

          <div class="w-full">
            <p v-if="detail?.status !== undefined" class="font-bold mb-3">
              {{ $t('page.status') }}:
              <span :class="statusView.class">
                {{ statusView.label }}
              </span>
            </p>

            <p v-if="rejectionReasonText" class="font-bold mb-3">
              {{ $t('page.reason') }}:
              <span class="text-red-500 font-medium" v-html="rejectionReasonText"></span>
            </p>
            <p></p>
            <div class="flex flex-col gap-3 mb-4">
              <div v-if="categoryDetail?.on_credit" class="flex items-center gap-2">
                {{ $t('page.onCredit') }} <IconChecked v-if="detail?.on_credit" />
                <IconUnchecked v-else />
              </div>
              <div v-if="categoryDetail?.on_exchange" class="flex items-center gap-2">
                {{ $t('page.onExchange') }} <IconChecked v-if="detail?.on_exchange" />
                <IconUnchecked v-else />
              </div>
              <div class="flex items-center gap-2">
                {{ $t('page.allowComments') }} <IconChecked v-if="detail?.allow_comments" />
                <IconUnchecked v-else />
              </div>
            </div>
            <p class="font-light text-[#333333] text-lg w-96 mb-5">
              {{ detail?.description }}
            </p>
            <div class="flex justify-end gap-5 mb-3">
              <button
                v-if="detail?.subscription !== ''"
                class="w-max rounded-lg px-8 font-bold"
                :class="getSubcriptionType?.color"
              >
                {{ getSubcriptionType?.name }}
              </button>
              <div
                v-if="detail?.is_vip"
                class="w-max px-8 bg-[#7951F9] rounded-lg text-white font-bold text-2xl text-end"
              >
                VIP
              </div>
            </div>
            <div class="bg-white divide-y rounded mb-5">
              <p class="text-main-color text-base font-semibold p-4">{{ $t('page.detail') }}</p>
              <InfoBlock :attributes="detail?.attributes" />
            </div>

            <button
              @click="deletePublication"
              class="bg-red-500 text-white w-full rounded py-3 hover:bg-red-600"
            >
              {{ $t('page.remove') }}
            </button>
          </div>
        </div>
      </div>
      <!--  -->
    </div>
  </div>
</template>
<script>
import api from '@/api';
import VueEasyLightbox from 'vue-easy-lightbox';
import IconChecked from '@icons/IconChecked.vue';
import IconUnchecked from '@icons/IconUnchecked.vue';
import { i18n } from '@/i18n';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { formatDate, formattedHour, formatNumber, isVideo } from '@/helpers';
import { MYPUBLICATION_STATUSES } from '@/constants';
import InfoBlock from '@cmp/InfoBlock.vue';

export default {
  name: 'viewMyPublication',
  components: {
    VueEasyLightbox,
    Splide,
    SplideSlide,
    SplideTrack,
    IconChecked,
    IconUnchecked,
    InfoBlock,
  },
  data() {
    return {
      titleFont: 30,
      titleFontMax: 30,
      titleFontMin: 18,
      _ro: null,
      _raf: null,
      currentId: null,
      detail: null,
      visibleRef: false,
      indexRef: 0,
      imgsRef: [],
      currentImage: null,
      imageOptions: {
        perPage: 3,
        perMove: 1,
        gap: '10px',
      },
      MYPUBLICATION_STATUSES: MYPUBLICATION_STATUSES,
      categoryDetail: {},
    };
  },
  methods: {
    isVideo,
    formatDate,
    formattedHour,
    formatNumber,
    buildImgsRef() {
      // lightbox только для картинок
      const images = Array.isArray(this.detail?.images) ? this.detail.images : [];
      this.imgsRef = images.filter(i => i?.path && !this.isVideo(i.path)).map(i => i.path);
    },

    syncIndexRef() {
      const idx = this.imgsRef.findIndex(p => p === this.currentImage);
      this.indexRef = idx >= 0 ? idx : 0;
    },

    onShow() {
      // не открываем lightbox для видео
      if (!this.currentImage || this.isVideo(this.currentImage)) return;
      this.buildImgsRef();
      this.syncIndexRef();
      this.visibleRef = true;
    },

    onHide() {
      this.visibleRef = false;
    },

    showSingle() {
      this.onShow();
    },

    changeImage(index) {
      const medias = Array.isArray(this.detail?.images) ? this.detail.images : [];
      this.currentImage = medias?.[index]?.path || null;

      // лайтбокс только для картинок
      this.buildImgsRef();
      this.syncIndexRef();

      // если выбрали видео — просто закрыть, но без return
      if (this.currentImage && this.isVideo(this.currentImage)) {
        this.visibleRef = false;
      }
    },
    handleImageError(event) {
      if (!event?.target) return;

      const fallback = '/placeholder-publication.png';

      if (!event.target.src.includes(fallback)) {
        event.target.src = fallback;
      }
    },

    async getMyPublicationRequest() {
      try {
        const response = await api.get(`publications/my-publication-requests/${this.currentId}/`);
        this.detail = response.data;

        await this.getCategoryDetail();
        this.$nextTick(() => {
          this.fitTitle();
          if (this._ro && this.$refs.titleWrap) this._ro.observe(this.$refs.titleWrap);
        });

        const medias = Array.isArray(this.detail?.images) ? this.detail.images : [];
        this.currentImage = medias?.[0]?.path || null;

        this.buildImgsRef();
        this.syncIndexRef();
      } catch (e) {
        console.log(e);
      }
    },
    async getCategoryDetail() {
      try {
        const response = await api.get(`publications/categories/${this.detail.category_id}/`);
        this.categoryDetail = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    async deletePublication() {
      try {
        const response = await api.delete(
          `publications/my-publication-requests/${this.currentId}/`,
        );
        this.$toast.info(i18n.global.t('warning.PUBLICATION_REMOVED'));
        this.$router.push({ name: 'publicationRequests' });
      } catch (e) {
        console.log(e);
      }
    },
    async fitTitle() {
      await this.$nextTick();

      const el = this.$refs.titleEl;
      if (!el) return;

      const fits = () => el.scrollWidth <= el.clientWidth;

      let lo = this.titleFontMin;
      let hi = this.titleFontMax;
      let best = lo;

      // если влезает на максимуме — ставим максимум и выходим
      el.style.fontSize = hi + 'px';
      if (fits()) {
        this.titleFont = hi;
        el.style.fontSize = hi + 'px';
        return;
      }

      // binary search
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        el.style.fontSize = mid + 'px';

        if (fits()) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      this.titleFont = best;
      el.style.fontSize = best + 'px';
    },
  },

  watch: {
    titleText() {
      this.$nextTick(() => this.fitTitle());
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (typeof ResizeObserver === 'undefined') return;

      this._ro = new ResizeObserver(() => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.fitTitle());
      });

      if (this.$refs.titleWrap) this._ro.observe(this.$refs.titleWrap);

      this.fitTitle();
    });
  },

  beforeUnmount() {
    if (this._ro) this._ro.disconnect();
    cancelAnimationFrame(this._raf);
  },

  computed: {
    titleText() {
      if (this.detail?.name) return this.detail.name;
      const props = Array.isArray(this.detail?.title_properties)
        ? this.detail.title_properties
        : [];
      return props
        .map(p => p?.value)
        .filter(Boolean)
        .join(' ');
    },

    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          return null;
      }
    },

    rejectionReasonText() {
      return this.detail?.standard_rejection_reason || this.detail?.rejection_reason || '';
    },

    statusView() {
      const locale = this.$i18n?.locale || 'ru';

      if (this.rejectionReasonText) {
        return {
          class: 'text-red-500',
          label: locale === 'tk' ? 'Ret edildi' : 'Отклонён',
        };
      }

      const fallback = this.MYPUBLICATION_STATUSES?.[this.detail?.status];
      return {
        class: fallback?.class || 'text-yellow-500',
        label: fallback?.label?.[locale] || (locale === 'tk' ? 'Garaşylýar' : 'Ожидается'),
      };
    },
  },

  created() {
    this.currentId = this.$route.params.id;
    this.getMyPublicationRequest();
  },
};
</script>

<style scoped></style>
